// Meta (Facebook) Pixel — browser-side only (no Conversions API). The pixel is
// gated on REACT_APP_FB_PIXEL_ID: with no ID set, every call here is a no-op, so
// dev and preview builds never load Meta's script or send events.
//
// Prices in this app are stored in cents (priceCents); Meta wants major units
// (e.g. 49.00), so callers pass values already divided by 100. Currency is ILS
// throughout the store.

const PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID;
const CURRENCY = 'ILS';

let initialized = false;

// When an admin is signed in we must not pollute Meta with the team's own
// browsing/checkout activity (admins create books for free, bypassing Stripe —
// see server/routes/checkout.js). The server-side Conversions API already skips
// these via order.isTest, but the browser pixel fires independently, so it gets
// its own gate here. setPixelSuppressed() is driven from auth state
// (client/src/state/auth.js). We seed it synchronously from localStorage so the
// flag is already correct on a cold page load — before the async /me round-trip
// resolves — and PageView/etc on the first route don't leak.
let suppressed = (() => {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('iamhero_pixel_off') === '1';
  } catch {
    return false;
  }
})();

// Lets callers skip work that only exists to feed Meta (e.g. the CAPI beacon
// twin of a pixel event) while the team's own browsing is suppressed.
export function isPixelSuppressed() {
  return suppressed;
}

export function setPixelSuppressed(value) {
  suppressed = !!value;
  try {
    if (typeof localStorage === 'undefined') return;
    if (suppressed) localStorage.setItem('iamhero_pixel_off', '1');
    else localStorage.removeItem('iamhero_pixel_off');
  } catch {
    /* ignore storage errors (private mode, etc.) */
  }
}

// Injects Meta's standard fbq bootstrap and initializes the pixel once. Safe to
// call repeatedly. The first PageView is intentionally NOT fired here — the
// route tracker fires PageView on every navigation (including the first), so
// firing it here too would double-count the landing page.
export function initPixel() {
  if (initialized || !PIXEL_ID || typeof window === 'undefined') return;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  initialized = true;
}

// Low-level passthrough. Silently no-ops until fbq is loaded (i.e. no ID set, or
// the script is still in flight — fbq queues calls itself once it exists).
function track(event, params, options) {
  if (suppressed) return;
  if (typeof window === 'undefined' || !window.fbq) return;
  if (options) window.fbq('track', event, params, options);
  else if (params) window.fbq('track', event, params);
  else window.fbq('track', event);
}

function clean(params) {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v != null));
}

// Reads Meta's first-party cookies so the server can forward them on the
// Conversions API Purchase for match quality (see server/services/metaCapi.js).
// _fbp is set by the pixel for every visitor once fbevents.js loads; _fbc only
// exists when the visitor arrived via a Meta ad click (carries the click id).
// Returns {} (no keys) when the cookies aren't present or there's no document.
export function getFbCookies() {
  if (typeof document === 'undefined') return {};
  const read = (name) => {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : undefined;
  };
  return clean({ fbp: read('_fbp'), fbc: read('_fbc') });
}

export function trackPageView() {
  track('PageView');
}

export function trackViewContent({ id, name, value }) {
  track('ViewContent', clean({
    content_ids: id ? [id] : null,
    content_name: name,
    content_type: 'product',
    value,
    currency: CURRENCY,
  }));
}

// Fired when a visitor completes the pre-preview contact step (name/email/phone)
// and we kick off story generation — the preview funnel's own lead signal,
// mapped to Meta's standard "CompleteRegistration". It fires here, before the
// (slow) generation, so the lead is counted even if the preview never finishes;
// AddToCart/InitiateCheckout/Purchase only come later at checkout. `contentName`
// segments by template; `status` marks the registration as completed.
export function trackCompleteRegistration({ contentName, value, eventId } = {}) {
  track('CompleteRegistration', clean({
    content_name: contentName,
    status: true,
    value,
    currency: CURRENCY,
  }), eventId ? { eventID: String(eventId) } : undefined);
}

// eventId, when passed, lets Meta dedupe this browser event against the matching
// server-side Conversions API event (see server/services/metaCapi.js
// sendCheckoutEvents). Both fire at checkout submit; the browser one is dropped
// often as the page unloads into the Stripe redirect, so the server copy is the
// reliable one and dedup keeps them counted as a single conversion.
export function trackAddToCart({ id, name, value, eventId }) {
  track('AddToCart', clean({
    content_ids: id ? [id] : null,
    content_name: name,
    content_type: 'product',
    value,
    currency: CURRENCY,
  }), eventId ? { eventID: String(eventId) } : undefined);
}

export function trackInitiateCheckout({ id, value, numItems = 1, eventId }) {
  track('InitiateCheckout', clean({
    content_ids: id ? [id] : null,
    content_type: 'product',
    num_items: numItems,
    value,
    currency: CURRENCY,
  }), eventId ? { eventID: String(eventId) } : undefined);
}

// eventID lets Meta dedupe if the success page is refreshed (it re-polls and
// could re-fire) and gives a stable id should CAPI be added later.
export function trackPurchase({ orderId, value, currency }) {
  track(
    'Purchase',
    clean({ value, currency: currency || CURRENCY, content_type: 'product' }),
    orderId ? { eventID: String(orderId) } : undefined,
  );
}
